import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { IconChecks, IconX } from '@tabler/icons-react';
import { Button } from '../../primitives/Button.js';
import { IconButton } from '../../primitives/IconButton.js';
import { Text } from '../../primitives/Text.js';
import { Tooltip } from '../../primitives/Tooltip.js';
import { WP_ICON_NAV } from '../../icons/constants.js';
import { cx } from '../../utils/cx.js';
import styles from './DataTableSelectionBar.module.css';

export type DataTableSelectionBarSelectAll = {
  total: number;
  onSelectAll: () => void;
  loading?: boolean;
  disabled?: boolean;
  label?: string;
  hint?: string;
};

export type DataTableSelectionBarProps = {
  count: number;
  /** Selected rows visible on the current page (for cross-page hint). */
  pageCount?: number;
  onClear: () => void;
  countLabel?: (count: number) => string;
  clearLabel?: string;
  crossPageHint?: (pageCount: number) => string;
  /**
   * `inline` — document-flow banner above siblings.
   * `dock` — content-width drawer portaled to the viewport bottom.
   */
  variant?: 'inline' | 'dock';
  /** Dock: fold “select all” into the count chip when selection is partial. */
  selectAll?: DataTableSelectionBarSelectAll;
  children?: ReactNode;
};

type DockPhase = 'idle' | 'enter' | 'visible' | 'exit';

const DOCK_EXIT_MS = 280;

function useDockPhase(count: number, variant: 'inline' | 'dock'): {
  phase: DockPhase;
  displayCount: number;
} {
  const [phase, setPhase] = useState<DockPhase>(count > 0 ? 'enter' : 'idle');
  const [frozenCount, setFrozenCount] = useState(count);
  const exitTimer = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    if (variant !== 'dock') return;

    if (count > 0) {
      if (exitTimer.current) {
        clearTimeout(exitTimer.current);
        exitTimer.current = undefined;
      }
      setFrozenCount(count);
      setPhase((prev) => (prev === 'visible' ? 'visible' : 'enter'));
      const id = requestAnimationFrame(() => {
        requestAnimationFrame(() => setPhase('visible'));
      });
      return () => cancelAnimationFrame(id);
    }

    setPhase((prev) => (prev === 'idle' ? 'idle' : 'exit'));
    exitTimer.current = setTimeout(() => {
      setPhase('idle');
      setFrozenCount(0);
    }, DOCK_EXIT_MS);
    return () => {
      if (exitTimer.current) clearTimeout(exitTimer.current);
    };
  }, [count, variant]);

  if (variant !== 'dock') {
    return { phase: count > 0 ? 'visible' : 'idle', displayCount: count };
  }
  if (phase === 'idle') return { phase, displayCount: 0 };
  return { phase, displayCount: count > 0 ? count : frozenCount };
}

export function DataTableSelectionBar({
  count,
  pageCount,
  onClear,
  countLabel = (n) => `${n} sélectionné${n > 1 ? 's' : ''}`,
  clearLabel = 'Effacer',
  crossPageHint = (onPage) =>
    `${onPage} sur cette page — parcourez les pages pour compléter la sélection`,
  variant = 'inline',
  selectAll,
  children,
}: DataTableSelectionBarProps) {
  const { phase, displayCount } = useDockPhase(count, variant);
  const isDock = variant === 'dock';

  if (variant === 'inline' && count <= 0) return null;
  if (isDock && phase === 'idle') return null;

  const showCrossPageHint =
    pageCount != null && pageCount < displayCount && pageCount >= 0;

  const selectAllTotal = selectAll?.total ?? 0;
  const showSelectAll =
    Boolean(selectAll) &&
    isDock &&
    selectAllTotal > 0 &&
    displayCount < selectAllTotal;

  const scopeLabel = showSelectAll
    ? `${displayCount} / ${selectAllTotal}`
    : countLabel(displayCount);

  const selectAllLabel = selectAll?.loading ? '…' : (selectAll?.label ?? 'Tous');
  const selectAllHint =
    selectAll?.hint ??
    (selectAllTotal > 0 ? `Sélectionner les ${selectAllTotal} éléments` : 'Sélectionner tout');

  const countBadge = (
    <span
      className={cx(styles.countBadge, showSelectAll && styles.countBadgeScope)}
      aria-live="polite"
    >
      <Text
        size={isDock ? 'xs' : 'sm'}
        fw={600}
        component="span"
        className={styles.count}
      >
        {scopeLabel}
      </Text>
      {showSelectAll && selectAll ? (
        <>
          <span className={styles.scopeDivider} aria-hidden />
          <Tooltip label={selectAllHint} position="top" portal>
            <button
              type="button"
              className={styles.scopeSelectAll}
              disabled={selectAll.disabled || selectAll.loading}
              aria-label={selectAllHint}
              onClick={selectAll.onSelectAll}
            >
              <IconChecks size={14} stroke={1.75} aria-hidden />
              <span className={styles.scopeSelectAllLabel}>{selectAllLabel}</span>
            </button>
          </Tooltip>
        </>
      ) : null}
    </span>
  );

  const dockClear = (
    <Tooltip label={clearLabel} position="top" portal>
      <IconButton
        type="button"
        size="xs"
        variant="soft"
        borderless
        aria-label={clearLabel}
        onClick={onClear}
        className={styles.dockControl}
      >
        <IconX {...WP_ICON_NAV} />
      </IconButton>
    </Tooltip>
  );

  const mainRow = (
    <div className={styles.mainRow}>
      <div className={styles.news}>
        {countBadge}
        {isDock ? null : (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onClear}
            className={styles.clearBtn}
          >
            {clearLabel}
          </Button>
        )}
      </div>
      {children ? (
        <>
          <span className={styles.dockDivider} aria-hidden />
          <div className={styles.actions} data-wp-dock-actions={isDock ? '' : undefined}>
            {children}
          </div>
        </>
      ) : null}
      {isDock ? (
        <>
          <span className={styles.dockDivider} aria-hidden />
          {dockClear}
        </>
      ) : null}
    </div>
  );

  if (isDock) {
    const dock = (
      <div
        className={styles.barDock}
        data-wp-datatable-selection-bar
        data-wp-datatable-selection-bar-variant={variant}
        data-phase={phase}
        role="region"
        aria-label="Actions sur la sélection"
      >
        {showCrossPageHint ? (
          <Text size="xs" c="dimmed" component="p" className={styles.dockHint}>
            {crossPageHint(pageCount)}
          </Text>
        ) : null}
        <div className={styles.dockPill}>{mainRow}</div>
      </div>
    );
    if (typeof document === 'undefined') return null;
    return createPortal(dock, document.body);
  }

  return (
    <div
      className={styles.bar}
      data-wp-datatable-selection-bar
      data-wp-datatable-selection-bar-variant={variant}
      role="region"
      aria-label="Actions sur la sélection"
    >
      {mainRow}
      {showCrossPageHint ? (
        <Text size="xs" c="dimmed" component="p" className={styles.hint}>
          {crossPageHint(pageCount)}
        </Text>
      ) : null}
    </div>
  );
}
